import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS, API_BASE_URL } from '../../constants/api-urls';
import { buildInspectionReportLocation } from '../../utils/qaInspectionFlow';
import './QADashboard.scss';

const QA_MY_INSPECTIONS_URL = API_URLS.QA?.GET_MY_INSPECTIONS || '/qa/inspector/my-inspections';
const API_BASE_URL_MEDIA = API_BASE_URL.replace('/api/v1', '');

const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return '';
  if (typeof accessPath === 'string' && (accessPath.startsWith('http://') || accessPath.startsWith('https://'))) return accessPath;
  const baseUrl = API_BASE_URL_MEDIA;
  const path = typeof accessPath === 'string' ? accessPath : (accessPath && accessPath.path ? accessPath.path : '');
  if (!path) return '';
  if (path.startsWith('/auction-media/')) return `${baseUrl}${path}`;
  const fileName = path.split(/[/\\]/).pop();
  if (fileName && auctionId) return `${baseUrl}/auction-media/${auctionId}/${fileName}`;
  return `${baseUrl}/media/${fileName || ''}`;
};

function normalizeQaInspectionsForList(qaList) {
  const isCmQaInspection = (ins) =>
    ins.context_source === 'contract_manufacturer' ||
    (ins.cm_rfq_id != null &&
      Number(ins.cm_rfq_id) > 0 &&
      ins.cm_quotation_id != null &&
      Number(ins.cm_quotation_id) > 0);

  return (qaList || []).map((ins) => {
    const isCm = isCmQaInspection(ins);
    const scope = ins.scope_logistics && typeof ins.scope_logistics === 'object' ? ins.scope_logistics : {};
    const nameFromScope =
      (typeof scope.product_name === 'string' && scope.product_name.trim()) ||
      (typeof scope.product_description === 'string' && scope.product_description.trim()) ||
      '';
    const productTitle =
      (typeof ins.product_name === 'string' && ins.product_name.trim()) ||
      nameFromScope ||
      'QA Inspection Service';
    const rfqLabel = isCm
      ? `CM${ins.cm_manufacturer_name ? ` · ${ins.cm_manufacturer_name}` : ''} · QA-${ins.qa_request_id || ins.inspection_id}`
      : `QA-${ins.qa_request_id || ins.inspection_id}`;

    return {
      ...ins,
      _source: 'qa',
      _isCmQa: isCm,
      product_name: productTitle,
      rfq_number: rfqLabel,
      preferred_time: ins.inspection_time,
      inspection_fee: ins.inspection_fee,
      currency: ins.currency || 'USD',
    };
  });
}

export default function QACompletedList() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompletedInspections();
  }, []);

  const fetchCompletedInspections = async () => {
    try {
      setLoading(true);
      const [auctionRes, qaRes] = await Promise.all([
        CommonService.get(API_URLS.AUCTION.GET_MY_INSPECTIONS).catch((e) => {
          console.warn('Auction inspections fetch failed:', e?.response?.status, e?.message);
          return { success: false, data: [] };
        }),
        CommonService.get(QA_MY_INSPECTIONS_URL).catch((e) => {
          console.warn('QA inspections fetch failed:', e?.response?.status, e?.message);
          return { success: false, data: [] };
        }),
      ]);

      const auctionList = Array.isArray(auctionRes?.data) ? auctionRes.data : [];
      let qaList = [];
      if (Array.isArray(qaRes?.data)) {
        qaList = qaRes.data;
      } else if (qaRes?.data && typeof qaRes.data === 'object' && Array.isArray(qaRes.data.inspections)) {
        qaList = qaRes.data.inspections;
      } else if (Array.isArray(qaRes)) {
        qaList = qaRes;
      }

      const qaNormalized = normalizeQaInspectionsForList(qaList);
      const combined = [...auctionList, ...qaNormalized];
      const completed = combined.filter((ins) => (ins.status || '').toLowerCase() === 'completed');

      completed.sort((a, b) => {
        const ta = new Date(a.completed_at || a.inspection_date || 0).getTime();
        const tb = new Date(b.completed_at || b.inspection_date || 0).getTime();
        return tb - ta;
      });

      setInspections(completed);
    } catch (error) {
      console.error('Error fetching completed inspections:', error);
      toast.error('Failed to load completed inspections');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    if (amount == null) return currency === 'USD' ? '$0' : '₹0';
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
    }
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getFirstImage = (inspection) => {
    const images = inspection.images;
    if (!images) return null;
    const arr = Array.isArray(images) ? images : (typeof images === 'string' ? (images.trim().startsWith('[') ? JSON.parse(images) : [images]) : []);
    const first = arr[0];
    if (!first) return null;
    const path = typeof first === 'string' ? first : (first && first.path ? first.path : first);
    return getMediaUrl(path, inspection.auction_id);
  };

  if (loading) {
    return (
      <div className="qa-dashboard loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="qa-dashboard">
      <div className="container-fluid">
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate('/auction/qa/dashboard')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Dashboard
          </button>
          <div className="header-content">
            <h1>Completed Inspections</h1>
            <p>Submitted inspection reports — auction inspections and buyer QA (including contract manufacturing).</p>
          </div>
        </div>

        <div className="rfqs-list">
          {inspections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M32 8C18.745 8 8 18.745 8 32C8 45.255 18.745 56 32 56C45.255 56 56 45.255 56 32C56 18.745 45.255 8 32 8ZM32 52C21.514 52 12 42.486 12 32C12 21.514 21.514 12 32 12C42.486 12 52 21.514 52 32C52 42.486 42.486 52 32 52Z" fill="currentColor"/>
                  <path d="M32 20V32M32 36H32.02" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No Completed Inspections</h3>
              <p>Your completed inspection reports will appear here.</p>
            </div>
          ) : (
            inspections.map((insp) => {
              const imgUrl = getFirstImage(insp);
              const rowKey = `${insp._source || 'auction'}-${insp.inspection_id}`;
              return (
                <div key={rowKey} className="rfq-card">
                  <div className="rfq-card-header">
                    <div className="rfq-info">
                      <div className="rfq-id">RFQ #{insp.rfq_number} · Inspection #{insp.inspection_id}</div>
                      {insp._isCmQa && (
                        <span className="qa-flow-badge qa-flow-badge--cm" title="Quality inspection tied to a contract-manufacturer RFQ">
                          CM QA
                        </span>
                      )}
                      <span className="badge badge-quoted">Completed</span>
                    </div>
                    <div className="rfq-date">Completed: {formatDate(insp.completed_at || insp.inspection_date)}</div>
                  </div>

                  <div className="rfq-card-body">
                    <div className="product-section">
                      <div className="product-image">
                        {imgUrl ? (
                          <img src={imgUrl} alt={insp.product_name} />
                        ) : (
                          <div className="placeholder-image">No Image</div>
                        )}
                      </div>
                      <div className="product-details">
                        <h3 className="product-name">{insp.product_name || 'Product'}</h3>
                        <div className="product-meta">
                          {insp.category_name && (
                            <span className="category">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M2 4H14M2 4V12C2 12.5304 2.21071 13.0391 2.58579 13.4142C2.96086 13.7893 3.46957 14 4 14H12C12.5304 14 13.0391 13.7893 13.4142 13.4142C13.7893 13.0391 14 12.5304 14 12V4M2 4L2 2C2 1.46957 2.21071 0.96086 2.58579 0.585786C2.96086 0.210714 3.46957 0 4 0H6M14 4V2C14 1.46957 13.7893 0.96086 13.4142 0.585786C13.0391 0.210714 12.5304 0 12 0H10M6 0V4M6 0H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              {insp.category_name}
                            </span>
                          )}
                          {insp.inspection_fee != null && (
                            <span className="bid-amount">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              Fee: {formatCurrency(insp.inspection_fee, insp.currency)}
                            </span>
                          )}
                        </div>
                        {insp.product_description && (
                          <p className="product-description">{String(insp.product_description).substring(0, 150)}...</p>
                        )}
                      </div>
                    </div>

                    <div className="inspection-details">
                      <div className="detail-item">
                        <div className="detail-icon">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 1C6.24 1 4 3.24 4 6C4 10.5 10 17 10 17C10 17 16 10.5 16 6C16 3.24 13.76 1 10 1ZM10 8.25C8.76 8.25 7.75 7.24 7.75 6C7.75 4.76 8.76 3.75 10 3.75C11.24 3.75 12.25 4.76 12.25 6C12.25 7.24 11.24 8.25 10 8.25Z" fill="currentColor"/>
                          </svg>
                        </div>
                        <div className="detail-content">
                          <div className="detail-label">Inspection Location</div>
                          <div className="detail-value">
                            {insp.inspection_location_address}
                            {insp.inspection_location_city || insp.inspection_location_state ? (
                              <><br />{insp.inspection_location_city}, {insp.inspection_location_state}</>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rfq-card-footer">
                    <button
                      className="btn-view-quotation"
                      onClick={() => navigate(buildInspectionReportLocation(insp.inspection_id, insp))}
                    >
                      View Report
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
